import React, { useEffect, useState } from "react";
import { ButtonGroup, Dropdown, Form } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { registerObject } from "go-form";
import useFlash from "go-alert/AlertMessage";
import FileUpload from "go-app/components/ImageForm/FileUpload";
import handleError from "go-app/services/errors";
import { ReactComponent as EmptyImageSVG } from "go-component/images/svg/angle-input.svg";
import handleException from "go-core/api/handleException";
import { ApiError, MoneyApi } from "go-core/api/types";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { ReactComponent as MoreSVG } from "go-core/images/svg/actions/more.svg";
import { FormDirty } from "go-form/components/FormDirty";
import FormMoneyInput from "go-form/components/FormMoneyInput";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { partnerApi } from "../../../../../../../../../../../services/Api/Partner/partnerApi";
import { PartnerPayoutInvoiceFormProps } from "../../../../../../../../../../../services/Api/Partner/partnerTypes";
import { FileAttachmentApi } from "../../../../../../../../../../../services/Api/types";
import { selectPartner } from "../../../../../../../../../../../services/partner/selectors";
import { downloadFileWithAccessToken } from "../../../../../../../../../../../utils";
import FileDisplay from "../../../../../../../../../components/FileUpload/FileDisplay";

interface Props {
	balance?: MoneyApi;
}

const PartnerPayoutInvoiceForm = (props: Props) => {
	const { t } = useTranslation("admin");
	const confirmation = useConfirmation();
	const partner = useSelector(selectPartner);
	const { addFlash, addSuccessFlash } = useFlash();
	const [attachmentError, setAttachmentError] = useState<ApiError | undefined>(undefined);
	const form = useForm<PartnerPayoutInvoiceFormProps>({
		criteriaMode: "all",
		defaultValues: {
			attachments: [],
			total_price: props.balance,
		},
	});
	const {
		formState,
		formState: { errors },
		control,
		register,
		setValue,
		handleSubmit,
		setError,
		reset,
		clearErrors,
	} = form;
	const [loading, setLoading] = useState<boolean>(false);
	const history = useHistory();
	const { append, remove, fields } = useFieldArray({
		keyName: "key",
		name: "attachments",
		control,
	});
	const placeholderTotalPrice = props.balance ? FormatMoney(props.balance) : "";
	const firstField = fields[0];

	useEffect(() => {
		fields.map((field) => registerFile(field));
	}, [fields]);

	const registerFile = (file: FileAttachmentApi) => {
		registerObject(register, `attachments[0]`, ["name", "content", "id"]);
		setValue(`attachments.0`, file);
	};

	const getBase64 = (file: File) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result);
			reader.onerror = (error) => reject(error);
		});
	};

	const onUpdateFile = async (file: File | undefined) => {
		if (!file) return;

		const fileAsBase64: any = await getBase64(file);
		const createdFile = {
			name: file.name,
			content: fileAsBase64.split(",")[1],
		};
		append(createdFile);
		clearErrors();
		setAttachmentError(undefined);
	};

	const onRemoveFile = async () => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.remove", { ns: "lib" }),
			});
			remove(0);
		} catch (err) {
			return null;
		}
	};

	const getParsedDataForm = (data: PartnerPayoutInvoiceFormProps) => {
		if (!data.attachments || data.attachments[0] === undefined) data.attachments = [];
		return {
			...data,
			name: data?.attachments[0].name,
			content: data?.attachments[0].content,
		};
	};

	const onSubmit = handleSubmit(async (data: PartnerPayoutInvoiceFormProps) => {
		setLoading(true);
		try {
			const newData = getParsedDataForm(data);
			await partnerApi.createPayoutInvoice(Number(partner?.id), newData);
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			reset();
			history.goBack();
		} catch (e) {
			handleError.form(e, setError, addFlash);
			const exceptionErrors = handleException(e);
			const attachmentError = exceptionErrors.find((error) => error.field === "name");
			if (attachmentError) {
				setAttachmentError(attachmentError);
			}
		}
		setLoading(false);
	});

	return (
		<FormDirty formState={formState} key="payout-invoice-form" onSubmit={onSubmit} loading={loading}>
			<fieldset>
				<div className="mt-3 col-lg-6 px-0">
					<FormMoneyInput
						currency={"PLN"}
						errors={errors}
						name="total_price.amount"
						control={control}
						onChange={() => clearErrors()}
						placeholder={placeholderTotalPrice}
					/>
				</div>

				<div className="invoice-attachment">
					{fields.length !== 1 && (
						<div className="file-attachment-wrapper mb-2">
							<FileUpload
								customString={t("modules.payoff.action.add_attachment.title")}
								onUpdateFile={(_, fileObj) => onUpdateFile(fileObj)}
							/>
						</div>
					)}
					<div className="is-invalid" />
					<Form.Control.Feedback type="invalid">
						{attachmentError && <div>{t("constraints.no_attachment")}</div>}
					</Form.Control.Feedback>
					{firstField && (
						<div className={"file-attachment-wrapper mb-2"}>
							<div className={"dropzone d-flex"}>
								<Dropdown onClick={(evt) => evt.stopPropagation()} as={ButtonGroup} className="actions">
									<Dropdown.Toggle draggable={false} as={MoreSVG}>
										{t("common.action.more", { ns: "lib" })}
									</Dropdown.Toggle>
									<Dropdown.Menu>
										<Dropdown.Item onClick={onRemoveFile}>
											{t("common.action.remove", { ns: "lib" })}
										</Dropdown.Item>
										<Dropdown.Item onClick={() => downloadFileWithAccessToken(firstField)}>
											{t("common.action.download")}
										</Dropdown.Item>
									</Dropdown.Menu>
								</Dropdown>
								<FileDisplay fileName={fields[0].name} icon={<EmptyImageSVG />} />
							</div>
						</div>
					)}
				</div>
			</fieldset>
		</FormDirty>
	);
};

export default PartnerPayoutInvoiceForm;
